import { useState } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { Divider, RadioButton, Card } from 'react-native-paper';
import { CustomInput, CustomButton, CustomDialog } from './index';
import { Svg, Path } from 'react-native-svg';
import { getAsyncStorage } from '../global/utils/asyncFun';
import useProduct from 'hooks/useProduct';
import BarcodeReader from 'react-barcode-reader';
import { ReactBarcode } from 'react-jsbarcode';

const styles = {
  details: {
    border: '1px solid #ccc',
    borderRadius: '12px',
    padding: '1rem',
    maxWidth: '600px',
    margin: '0.5rem auto',
    backgroundColor: '#f9f9f9',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
    fontFamily: 'Segoe UI, sans-serif',
    fontSize: '16px',
    color: '#333',
    cursor: 'pointer',
    transition: 'all 0.3s ease-in-out',
    width: '100%',
  },
  summary: {
    fontWeight: 600,
    fontSize: '18px',
    marginBottom: '0.5rem',
    outline: 'none',
  },
  answer: {
    marginTop: '1rem',
    padding: '0.5rem 0',
    color: '#555',
    fontStyle: 'italic',
  },
};

interface IProductInfo {
  product_category_id: any;
}

const CustomAdminBulkProductUpload = ({ product_category_id }: IProductInfo) => {
  const { createBulkProductItemManually } = useProduct();
  const [activeAccordion, setActiveAccordion] = useState<number | null>(0);
  const [productInfo, setProductInfo] = useState([
    {
      name: '',
      description: '',
      stock: '',
      stock_value: '',
      price: '',
      cost_per_unit: '',
      expiry_date: '',
      wholesale_price: '',
      discount_unit: '',
      discount_price: '',
      is_single: '0',
      barcode: [],
    },
  ]);

  // Validation function
  const validateProduct = (product: any) => {
    // Check required fields
    if (!product.name || !product.stock || !product.cost_per_unit || !product.stock_value) {
      return false;
    }

    // Check barcode when is_single is '1'
    if (product.is_single === '1' && (!product.barcode || product.barcode.length === 0)) {
      return false;
    }

    // Check discount validation - if either discount_price or discount_unit is not empty, both should be filled
    if ((product.discount_price && !product.discount_unit) || (!product.discount_price && product.discount_unit)) {
      return false;
    }

    return true;
  };

  // Check if all products are valid
  const isFormValid = () => {
    return productInfo.every(validateProduct);
  };

  const onChange = (e: any, index: number, title: string) => {
    const updatedProducts = [...productInfo];
    updatedProducts[index] = { ...updatedProducts[index], [title]: e };
    setProductInfo(updatedProducts);
  };

  const addNewProduct = () => {
    const emptyProduct = {
      name: '',
      description: '',
      stock: '',
      stock_value: '',
      price: '',
      cost_per_unit: '',
      expiry_date: '',
      wholesale_price: '',
      discount_unit: '',
      discount_price: '',
      is_single: '0',
      barcode: [],
    };
    setProductInfo((prev) => [...prev, { ...emptyProduct }]);
  };

  const removeProduct = (index: number) => {
    setProductInfo((prev) => prev.filter((_, i) => i !== index));
  };

  const removeBarcode = (index: number) => {
    const updatedProducts = [...productInfo];
    updatedProducts[index].barcode = []
    setProductInfo(updatedProducts);
  };

  const onClearFun = () => {
    setProductInfo([
      {
        name: '',
        description: '',
        stock: '',
        stock_value: '',
        price: '',
        cost_per_unit: '',
        expiry_date: '',
        wholesale_price: '',
        discount_unit: '',
        discount_price: '',
        is_single: '0',
        barcode: [],
      },
    ]);
  };

  const onSubmit = async () => {
    try {
      let shopId = await getAsyncStorage('shop');
      let dataToSubmit = productInfo.map((data) => ({
        name: data?.name,
        shop_id: shopId?.id,
        is_single: data?.is_single,
        description: data.description,
        category_id: product_category_id,
        stock: Number(data?.stock),
        stock_value: Number(data?.stock_value),
        cost_per_unit: Number(data?.cost_per_unit),
        expiry_date: data?.expiry_date,
        wholesale_price: Number(data?.wholesale_price),
        discount_unit: data?.discount_unit,
        discount_price: Number(data?.discount_price),
        barcode: data?.barcode ?? '',
        price: Number(data?.price),
      }));
      console.log('product_category_id', product_category_id);
      createBulkProductItemManually(dataToSubmit, onClearFun);
    } catch (error) {}
  };

  return (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
      {productInfo.map((item, index) => (
        <View key={index} style={{ width: '50%' }}>
          {index !== 0 && (
            <TouchableOpacity
              style={{ flexDirection: 'row-reverse', marginBottom: -5 }}
              onPress={() => removeProduct(index)}
            >
              <Svg width="30" height="30" viewBox="0 0 49 48" fill="none">
                <Path
                  d="M24.7827 4C13.7227 4 4.78271 12.94 4.78271 24C4.78271 35.06 13.7227 44 24.7827 44C35.8427 44 44.7827 35.06 44.7827 24C44.7827 12.94 35.8427 4 24.7827 4ZM24.7827 40C15.9627 40 8.78271 32.82 8.78271 24C8.78271 15.18 15.9627 8 24.7827 8C33.6027 8 40.7827 15.18 40.7827 24C40.7827 32.82 33.6027 40 24.7827 40ZM31.9627 14L24.7827 21.18L17.6027 14L14.7827 16.82L21.9627 24L14.7827 31.18L17.6027 34L24.7827 26.82L31.9627 34L34.7827 31.18L27.6027 24L34.7827 16.82L31.9627 14Z"
                  fill="#1E1E1E"
                />
              </Svg>
            </TouchableOpacity>
          )}
          <details onClick={() => setActiveAccordion(index)} style={styles.details}>
            <summary style={styles.summary}>Product {index + 1}</summary>
            <Divider />
            <div style={styles.answer}>
              <View style={{ marginBottom: 20, flexDirection: 'row', justifyContent: 'space-around' }}>
                <View style={{ width: '45%' }}>
                  <CustomInput
                    value={item.name}
                    placeholder="Product Name"
                    onChangeText={(e) => onChange(e, index, 'name')}
                    inputMode="text"
                    label="Product Name"
                  />
                </View>
                <View style={{ width: '45%' }}>
                  <CustomInput
                    value={item.price}
                    placeholder="Product Price"
                    onChangeText={(e) => onChange(e, index, 'price')}
                    inputMode="text"
                    label="Product Price"
                  />
                </View>
              </View>

              <View style={{ marginBottom: 20, flexDirection: 'row', justifyContent: 'space-around' }}>
                <View style={{ width: '45%' }}>
                  <CustomInput
                    value={item.cost_per_unit}
                    placeholder="Product Cost Price Per Unit"
                    onChangeText={(e) => onChange(e, index, 'cost_per_unit')}
                    inputMode="text"
                    label="Product Cost Price Per Unit"
                  />
                </View>
                <View style={{ width: '45%' }}>
                  <CustomInput
                    value={item.wholesale_price}
                    placeholder="Wholesale Price (Optional)"
                    onChangeText={(e) => onChange(e, index, 'wholesale_price')}
                    inputMode="text"
                    label="Wholesale Price"
                  />
                </View>
              </View>

              <View style={{ marginBottom: 20, flexDirection: 'row', justifyContent: 'space-around' }}>
                <View style={{ width: '45%' }}>
                  <CustomInput
                    value={item.discount_price}
                    placeholder="Discount Price  (Optional)"
                    onChangeText={(e) => onChange(e, index, 'discount_price')}
                    inputMode="text"
                    label="Discount Price"
                  />
                </View>
                <View style={{ width: '45%' }}>
                  <CustomInput
                    value={item.discount_unit}
                    placeholder="Discount Unit  (Optional)"
                    onChangeText={(e) => onChange(e, index, 'discount_unit')}
                    inputMode="text"
                    label="Discount Unit"
                  />
                </View>
              </View>

              <View style={{ marginBottom: 20, flexDirection: 'row', justifyContent: 'space-around' }}>
                <View style={{ width: '45%' }}>
                  <View style={{ marginBottom: 20 }}>
                    <Text
                      style={{
                        color: '#271E00',
                        fontSize: 13,
                        fontWeight: '600',
                        height: 24,
                        lineHeight: 24,
                      }}
                    >
                      Expiry Date(Optional)
                    </Text>
                    <input
                      // value={item.expiry_date}
                      placeholder="(Optional)"
                      onChange={(e) => onChange(e, index, 'expiry_date')}
                      type="date"
                      style={{
                        height: 25,
                        borderWidth: 0.3,
                        padding: 13,
                        borderColor: '#271E00',
                        borderRadius: 10,
                        color: '#3D3D3D',
                        fontSize: 14,
                        fontWeight: '500',
                        backgroundColor: '#efeded',
                      }}
                    />
                  </View>
                </View>
                <View style={{ width: '45%' }}>
                  <CustomInput
                    value={item.description}
                    placeholder="Product Description  (Optional)"
                    onChangeText={(e) => onChange(e, index, 'description')}
                    inputMode="text"
                    label="Product Description"
                  />
                </View>
              </View>
              <View style={{ marginTop: 20, marginBottom: 20, justifyContent: 'center', alignItems: 'center' }}>
                <Text
                  style={{
                    color: '#271E00',
                    fontSize: 13,
                    fontWeight: 'bold',
                    height: 24,
                    lineHeight: 24,
                  }}
                >
                  Select Stock Type
                </Text>
                <RadioButton.Group onValueChange={(e) => onChange(e, index, 'stock_value')} value={item.stock_value}>
                  <View style={{ flexDirection: 'row' }}>
                    <View style={{ marginRight: 10 }}>
                      <Text>(recommended for unit products)</Text>
                      <RadioButton.Item
                        labelStyle={{ fontWeight: 'bold', fontSize: 17, color: '#1E1E1E' }}
                        label="Pieces"
                        value="0"
                      />
                    </View>
                    <View style={{ marginLeft: 10 }}>
                      <Text>(recommended for scaled products)</Text>
                      <RadioButton.Item
                        labelStyle={{ fontWeight: 'bold', fontSize: 17, color: '#1E1E1E' }}
                        label="Kilogram"
                        value="1"
                      />
                    </View>
                  </View>
                </RadioButton.Group>
              </View>
              <View style={{ marginBottom: 20, flexDirection: 'row', justifyContent: 'space-around' }}>
                <View style={{ width: '45%' }}>
                  <CustomInput
                    value={item.stock}
                    placeholder="Product Stock"
                    onChangeText={(e) => onChange(e, index, 'stock')}
                    inputMode="numeric"
                    label="Product Stock"
                  />
                </View>
                <View style={{ width: '45%' }}>
                  <Text
                    style={{
                      color: '#271E00',
                      fontSize: 13,
                      fontWeight: '600',
                      height: 24,
                      lineHeight: 24,
                    }}
                  >
                    Barcode Product
                  </Text>
                  <RadioButton.Group onValueChange={(e) => onChange(e, index, 'is_single')} value={item.is_single}>
                    <View style={{ flexDirection: 'row' }}>
                      <View style={{ marginRight: 5 }}>
                        <RadioButton.Item
                          labelStyle={{ fontWeight: 'bold', fontSize: 17, color: '#1E1E1E' }}
                          label="No"
                          value="0"
                        />
                      </View>
                      <View style={{ marginLeft: 5 }}>
                        <RadioButton.Item
                          labelStyle={{ fontWeight: 'bold', fontSize: 17, color: '#1E1E1E' }}
                          label="Yes"
                          value="1"
                        />
                      </View>
                    </View>
                  </RadioButton.Group>
                </View>
                {activeAccordion === index && (
                  <>
                    {item.is_single === '1' && (
                      <BarcodeReader
                        onError={(err: any) => console.error(err)}
                        onScan={(e: any) => onChange([e], index, 'barcode')}
                      />
                    )}
                  </>
                )}
              </View>
              <View style={{ justifyContent: 'center' }}>
                {item?.barcode?.map((data, _index) => (
                  <Card
                    style={{
                      backgroundColor: '#ffffff',
                      height: 170,
                      width: 320,
                      marginTop: 5,
                      marginBottom: 5,
                      marginRight: 5,
                      marginLeft: 5,
                    }}
                    mode="elevated"
                  >
                    <Card.Content>
                      <View style={{ alignItems: 'center' }}>
                        <View style={{ marginLeft: 10 }}></View>
                        <ReactBarcode value={data} options={{ height: 50 }} />
                        <TouchableOpacity
                          onPress={() => removeBarcode(index)}
                          style={{
                            backgroundColor: 'rgba(183, 35, 0, 1)',
                            padding: 15,
                            borderRadius: 10,
                            width: 290,
               
                          }}
                        >
                          <Text style={{ color: '#ffffff', textAlign: 'center', fontWeight: '700' }}>Remove</Text>
                        </TouchableOpacity>
                      </View>
                    </Card.Content>
                  </Card>
                ))}
              </View>
            </div>
          </details>
        </View>
      ))}
      <View style={{ marginTop: 20, flexDirection: 'row', justifyContent: 'center' }}>
        <CustomButton
          fontSize={14}
          padding={5}
          width={'80%'}
          bntType="primary"
          mode="outlined"
          text="Add More Products"
          disable={!isFormValid()}
          // loading={authLoading}
          onPress={addNewProduct}
        />
        <View style={{ marginLeft: 10, marginRight: 10 }} />
        <CustomButton
          fontSize={14}
          padding={5}
          width={'80%'}
          bntType="primary"
          mode="contained"
          text="Create Products"
          disable={!isFormValid()}
          // loading={authLoading}
          onPress={onSubmit}
        />
      </View>
    </View>
  );
};

export default CustomAdminBulkProductUpload;
